import qrcode from 'qrcode-generator';
import {
  type CompositionDoc,
  type CompositionElement,
  type CompositionElementKind,
  CompositionElementSchema,
  type DataTable,
  type FontRecord,
  type MediaAssetRecord,
  qrMatrixSvg,
  type Sticker,
} from '@codot/contracts';
import runtimeJs from '@codot/contracts/composition-runtime/composition.js?raw';
import { ref } from 'vue';
import { firstLocatedScreen } from '../api/screens.api';

/** A short element id, unique enough inside one document. */
export function newElementId(doc: CompositionDoc): string {
  for (;;) {
    const id = Math.random().toString(36).slice(2, 10);
    if (!doc.elements.some((e) => e.id === id)) return id;
  }
}

export const ELEMENT_ICONS: Record<CompositionElementKind, string> = {
  text: 'T',
  shape: '◼',
  sticker: '★',
  drawing: '✎',
  image: '🖼',
  video: '🎬',
  clock: '🕒',
  date: '📅',
  countdown: '⏳',
  qr: '▦',
  weather: '⛅',
};

/** A new element of `kind`, centred on the canvas and sized for it. */
export function makeElement(doc: CompositionDoc, kind: CompositionElementKind, extra: Record<string, unknown> = {}): CompositionElement {
  const s = Math.min(doc.width, doc.height);
  const box = (w: number, h: number) => ({ width: Math.round(w), height: Math.round(h), x: Math.round((doc.width - w) / 2), y: Math.round((doc.height - h) / 2) });
  const id = newElementId(doc);
  const base: Record<string, unknown> = { id, kind };
  const text = { fontSize: Math.round(s / 12) };
  switch (kind) {
    case 'text':
      Object.assign(base, box(doc.width * 0.6, s / 6), text, { text: 'Your text' });
      break;
    case 'shape':
      Object.assign(base, box(s / 3, s / 3), { shape: 'rect' });
      break;
    case 'sticker':
      Object.assign(base, box(s / 4, s / 4), { sticker: 'star' });
      break;
    case 'image':
    case 'video':
      Object.assign(base, box(doc.width / 2, doc.height / 2));
      break;
    case 'clock':
    case 'date':
    case 'weather':
      Object.assign(base, box(s / 2, s / 8), text);
      break;
    case 'countdown':
      // Seconds on: a new countdown visibly ticks.
      Object.assign(base, box(doc.width * 0.5, s / 8), text, { target: new Date(Date.now() + 7 * 86_400_000).toISOString(), showSeconds: true });
      break;
    case 'qr':
      Object.assign(base, box(s / 4, s / 4), { data: 'https://example.com' });
      break;
    case 'drawing':
      // Normally made with the pen (drawingFromStroke); a placeholder squiggle otherwise.
      Object.assign(base, box(s / 3, s / 6), { strokes: [[0, 0.8, 0.25, 0.2, 0.5, 0.8, 0.75, 0.2, 1, 0.8]] });
      break;
  }
  return CompositionElementSchema.parse({ ...base, ...extra });
}

/**
 * A drawing element from one pen stroke (canvas pixels, x0, y0, x1, y1…): boxed around the stroke (with room for its
 * width) and stored as fractions of that box. Points closer than ~2 px are dropped to keep documents small.
 */
export function drawingFromStroke(doc: CompositionDoc, pts: number[], color: string, strokeWidth: number): CompositionElement | null {
  const kept: number[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2) {
    const n = kept.length;
    if (n && Math.hypot(pts[i]! - kept[n - 2]!, pts[i + 1]! - kept[n - 1]!) < 2) continue;
    kept.push(pts[i]!, pts[i + 1]!);
  }
  if (kept.length < 4) return null;
  const xs = kept.filter((_, i) => i % 2 === 0);
  const ys = kept.filter((_, i) => i % 2 === 1);
  const pad = strokeWidth / 2;
  const x = Math.min(...xs) - pad;
  const y = Math.min(...ys) - pad;
  const width = Math.max(1, Math.max(...xs) + pad - x);
  const height = Math.max(1, Math.max(...ys) + pad - y);
  const r = (v: number) => Math.min(1, Math.max(0, Math.round(v * 10_000) / 10_000));
  const stroke = kept.slice(0, 4000).map((v, i) => r(i % 2 === 0 ? (v - x) / width : (v - y) / height));
  return CompositionElementSchema.parse({ id: newElementId(doc), kind: 'drawing', x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height), strokes: [stroke], color, strokeWidth });
}

export const STICKER_LABELS: Record<Sticker, string> = { star: '★', heart: '♥', check: '✔', arrow: '➜', burst: '✸', badge: '⬭', tag: '🏷', bubble: '💬' };

/* ---------------- history ---------------- */

/** Undo/redo by snapshots (documents are small). Consecutive changes with the same `key` (a drag) merge into one step. */
export class History {
  private past: string[] = [];
  private future: string[] = [];
  private lastKey: string | null = null;
  private lastAt = 0;

  record(before: CompositionDoc, key: string | null = null): void {
    const now = Date.now();
    if (key && key === this.lastKey && now - this.lastAt < 1500) {
      this.lastAt = now;
      return;
    }
    this.past.push(JSON.stringify(before));
    if (this.past.length > 200) this.past.shift();
    this.future = [];
    this.lastKey = key;
    this.lastAt = now;
  }
  undo(current: CompositionDoc): CompositionDoc | null {
    const prev = this.past.pop();
    if (!prev) return null;
    this.future.push(JSON.stringify(current));
    this.lastKey = null;
    return JSON.parse(prev) as CompositionDoc;
  }
  redo(current: CompositionDoc): CompositionDoc | null {
    const next = this.future.pop();
    if (!next) return null;
    this.past.push(JSON.stringify(current));
    this.lastKey = null;
    return JSON.parse(next) as CompositionDoc;
  }
  get canUndo(): boolean {
    return this.past.length > 0;
  }
  get canRedo(): boolean {
    return this.future.length > 0;
  }
}

/* ---------------- geometry ---------------- */

/** Is canvas point (px, py) inside the (rotated) element? */
export function hits(e: CompositionElement, px: number, py: number): boolean {
  const cx = e.x + e.width / 2;
  const cy = e.y + e.height / 2;
  const a = (-e.rotation * Math.PI) / 180;
  const dx = px - cx;
  const dy = py - cy;
  const x = dx * Math.cos(a) - dy * Math.sin(a);
  const y = dx * Math.sin(a) + dy * Math.cos(a);
  return Math.abs(x) <= e.width / 2 && Math.abs(y) <= e.height / 2;
}

/** Snap lines: the canvas edges and centre, and every other visible element's edges and centre. */
export function snapTargets(doc: CompositionDoc, skipId: string): { xs: number[]; ys: number[] } {
  const xs = [0, doc.width / 2, doc.width];
  const ys = [0, doc.height / 2, doc.height];
  for (const e of doc.elements) {
    if (e.id === skipId || e.hidden || e.rotation % 360 !== 0) continue;
    xs.push(e.x, e.x + e.width / 2, e.x + e.width);
    ys.push(e.y, e.y + e.height / 2, e.y + e.height);
  }
  return { xs, ys };
}

/** The best snap of any of `edges` to `targets` within `tolerance`: the offset to apply and the line, or null. */
export function snap(edges: number[], targets: number[], tolerance: number): { delta: number; line: number } | null {
  let best: { delta: number; line: number } | null = null;
  for (const e of edges) {
    for (const t of targets) {
      const d = t - e;
      if (Math.abs(d) <= tolerance && (!best || Math.abs(d) < Math.abs(best.delta))) best = { delta: d, line: t };
    }
  }
  return best;
}

/* ---------------- what the runtime gets ---------------- */

export function qrSvg(data: string, color: string, background: string): string {
  const q = qrcode(0, 'M');
  // Byte mode takes one byte per character: hand it the UTF-8 bytes (same as the API's bundler).
  q.addData(Array.from(new TextEncoder().encode(data), (b) => String.fromCharCode(b)).join(''), 'Byte');
  q.make();
  return qrMatrixSvg(q.getModuleCount(), (r, c) => q.isDark(r, c), color, background);
}

export interface RuntimeData {
  doc: CompositionDoc;
  media: Record<string, string>;
  fonts: Record<string, { family: string; url: string }>;
  qr: Record<string, string>;
}

/** The `window.COMPOSITION` the runtime draws: media and fonts from the API (players get local copies). */
export function runtimeData(doc: CompositionDoc, library: Map<string, MediaAssetRecord>, fonts: FontRecord[]): RuntimeData {
  const media: Record<string, string> = {};
  const ids = [doc.backgroundAssetId, ...doc.elements.map((e) => ('assetId' in e ? e.assetId : null))];
  for (const id of ids) {
    const a = id ? library.get(id) : undefined;
    if (a?.fileUrl) media[a.id] = a.fileUrl;
  }
  const usedFonts: RuntimeData['fonts'] = {};
  for (const e of doc.elements) {
    if (!('fontId' in e) || !e.fontId) continue;
    const f = fonts.find((x) => x.id === e.fontId);
    if (f) usedFonts[f.id] = { family: `codot-${f.id}`, url: f.fileUrl };
  }
  const qr: Record<string, string> = {};
  for (const e of doc.elements) if (e.kind === 'qr') qr[e.id] = qrSvg(e.data, e.color, e.background);
  return { doc, media, fonts: usedFonts, qr };
}

/** Feed tables in the shape the runtime reads (`{columns, rows}` by feed id). */
export type RuntimeFeeds = Record<string, Pick<DataTable, 'columns' | 'rows'>>;

/** The iframe document: the real runtime, with the data inline. */
export function runtimeSrcdoc(data: RuntimeData): string {
  const json = JSON.stringify(data).replace(/</g, '\\u003c');
  return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;width:100%;height:100%;overflow:hidden;background:transparent}</style></head><body><script>window.COMPOSITION=${json};</script><script>${runtimeJs.replace(/<\/script/gi, '<\\/script')}</script></body></html>`;
}

/** Canvas size presets. */
export const CANVAS_PRESETS = [
  { label: 'Full HD landscape', width: 1920, height: 1080 },
  { label: 'Full HD portrait', width: 1080, height: 1920 },
  { label: '4K landscape', width: 3840, height: 2160 },
  { label: '4K portrait', width: 2160, height: 3840 },
  { label: 'Square', width: 1080, height: 1080 },
] as const;

/**
 * What the CMS passes the runtime instead of a player's settings: weather set to "each screen's location" needs a
 * place outside a player too — the first player with a location, else Dubai. Loaded once, shared by every preview.
 */
const previewSettingsRef = ref<Record<string, unknown>>({ timezone: null, latitude: 25.2, longitude: 55.27 });
let previewLoaded = false;
export function usePreviewSettings() {
  if (!previewLoaded) {
    previewLoaded = true;
    void firstLocatedScreen()
      .then((s) => {
        if (s) previewSettingsRef.value = { timezone: null, latitude: s.latitude, longitude: s.longitude };
      })
      .catch(() => undefined);
  }
  return previewSettingsRef;
}
